<template lang="pug">
.payment-details
  //- Payment Header
  PaymentDetailsHeader(:data="data" :hasCardDetails="hasCardDetails")
  
  //- Billing Information
  PaymentDetailsBilling(:billing="billing")
  
  //- Card Details
  PaymentDetailsCard(
    v-if="hasCardDetails"
    :card="card"
    :hideCardVisual="hideCardVisual"
    :isStripePayment="isStripePayment"
    :cardHolderName="cardHolderName"
    :data="data"
  )
  
  //- Paymentwall Details
  PaymentDetailsPaymentwall(:data="data")
  
  //- PayPal Details
  PaymentDetailsPayPal(:data="data")
  
  //- Raw JSON Display (collapsible)
  div(v-if="showRawJson" class="mt-4 border rounded-lg")
    button(@click="rawJsonExpanded = !rawJsonExpanded" class="w-full px-4 py-2 text-left text-sm font-semibold flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors")
      span Raw Data
      svg(:class="{ 'rotate-90': rawJsonExpanded }" class="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24")
        path(stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7")
    div(v-show="rawJsonExpanded" class="border-t")
      .relative
        button(@click="copyJson" class="absolute top-2 right-2 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded transition-colors z-10")
          | {{ copied ? 'Copied!' : 'Copy' }}
        pre.p-4.overflow-auto.text-xs(class="bg-gray-50 dark:bg-gray-900 max-h-96") {{ jsonString }}
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import PaymentDetailsHeader from './payment/PaymentDetailsHeader.vue'
import PaymentDetailsBilling from './payment/PaymentDetailsBilling.vue'
import PaymentDetailsCard from './payment/PaymentDetailsCard.vue'
import PaymentDetailsPaymentwall from './payment/PaymentDetailsPaymentwall.vue'
import PaymentDetailsPayPal from './payment/PaymentDetailsPayPal.vue'
import { myunescape } from '~/utils/escape'

const props = defineProps<{
  data: any
  showRawJson?: boolean
  hideCardVisual?: boolean
}>()

const copied = ref(false)
const rawJsonExpanded = ref(false)

// Extract billing information
const billing = computed(() => props.data?.billing || null)

// Check if this is a Stripe payment
const isStripePayment = computed(() => {
  return !!(props.data?.pi_id || props.data?.payment_id || props.data?.session_id || 
           props.data?.extra?.project_name || 
           (props.data?.channel && props.data.channel.toLowerCase().includes('stripe')))
})

// Extract card details from various possible locations
const card = computed(() => {
  // Try to find card details in different locations
  if (props.data?.card) return props.data.card
  if (props.data?.extra?.card_details) return props.data.extra.card_details
  if (props.data?.extra?.card) return props.data.extra.card
  if (props.data?.card_details) return props.data.card_details
  
  // Build card object from individual fields in extra
  const extra = props.data?.extra || {}
  if (extra.card_last4 || extra.card_brand || extra.card_fingerprint) {
    return {
      last4: extra.card_last4,
      brand: extra.card_brand,
      exp_month: extra.card_exp_month,
      exp_year: extra.card_exp_year,
      country: extra.card_country,
      funding: extra.card_funding,
      fingerprint: extra.card_fingerprint || extra.fingerprint,
      cvc_check: extra.cvc_check,
      address_zip: extra.address_zip,
      address_zip_check: extra.address_zip_check,
      address_line1_check: extra.address_line1_check,
      address_postal_code_check: extra.address_postal_code_check,
      checks: {
        cvc_check: extra.cvc_check,
        address_line1_check: extra.address_line1_check,
        address_postal_code_check: extra.address_postal_code_check
      },
      three_d_secure: extra.three_d_secure,
      three_d_secure_usage: extra.three_d_secure_usage
    }
  }
  
  return {}
})

const hasCardDetails = computed(() => {
  return !!(card.value && (card.value.last4 || card.value.brand || card.value.fingerprint))
})

// Use billing name for card holder name
const cardHolderName = computed(() => {
  if (!billing.value) return 'CARD HOLDER'
  const firstName = myunescape(billing.value.firstname || '')
  const lastName = myunescape(billing.value.lastname || '')
  const name = [firstName, lastName].filter(Boolean).join(' ')
  return name || 'CARD HOLDER'
})

// JSON display
const jsonString = computed(() => JSON.stringify(props.data, null, 2))

async function copyJson() {
  try {
    await navigator.clipboard.writeText(jsonString.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}
</script>